/**
 * QuickAuthModal — compact sign-in / registration popover triggered by
 * the header. Three tabs (Google / Phone / Email link) that reuse the
 * existing AuthContext primitives. No route change, no scroll loss.
 *
 * When ``mode === "register"`` the modal additionally collects the
 * user's First name, Last name, Gender, Year of Birth and asks them to
 * confirm their geo-detected location. After OAuth returns successfully,
 * those fields are PATCHed to ``/api/users/me/profile`` and merged into
 * the user document.
 *
 * Contract:
 *   props.onClose()      — called on any successful sign-in and on manual close.
 *   props.defaultTab     — "google" | "phone" | "email" (default "google").
 *   props.mode           — "signin" | "register" (default "signin").
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { X, Chrome, Phone as PhoneIcon, Mail, Loader2, ArrowRight, MapPin, Check, Building2, User } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useGeo } from "@/lib/useGeo";
import { http } from "@/lib/api";
import { resolveSiteSlug } from "@/config/siteConfig";
import { COUNTRY_CODES, NATIONAL_FORMATS, formatNational, sanitizeToE164 } from "@/lib/phoneFormat";

// Policy slugs the user MUST accept before finishing registration. The
// list is site-aware — IndianFamilyTree.com surfaces the full DPDP-
// aligned bundle; the default Social Pulse surface asks for the
// canonical Terms + Privacy + Cookie set.
const REQUIRED_POLICIES_DEFAULT = [
  { slug: "terms-of-use",         label: "Terms of Use" },
  { slug: "privacy-policy",       label: "Privacy Policy" },
  { slug: "cookie-policy",        label: "Cookie Policy" },
  { slug: "memorial-policy",      label: "Memorial & Obituary Policy" },
];
const REQUIRED_POLICIES_IFT = [
  { slug: "ift-terms-of-service", label: "Terms of Service" },
  { slug: "ift-privacy-policy",   label: "Privacy Policy" },
  { slug: "ift-cookie-policy",    label: "Cookie Policy" },
  { slug: "ift-consent-policy",   label: "Consent & Authorization" },
  { slug: "ift-family-tree-policy", label: "Family Tree Data & Accuracy" },
  { slug: "ift-memorial-policy",  label: "Memorial & Obituary Content" },
  { slug: "ift-deletion-legacy-policy", label: "Data Deletion & Legacy" },
];
// FoodPulse — reuses the generic Terms of Use until a FoodPulse-specific
// terms page is authored; Privacy + Cookie are already FoodPulse-branded.
const REQUIRED_POLICIES_FOODPULSE = [
  { slug: "terms-of-use",              label: "Terms of Use" },
  { slug: "foodpulse-privacy-policy",  label: "Privacy Policy (Food Pulse)" },
  { slug: "foodpulse-cookie-policy",   label: "Cookie Policy (Food Pulse)" },
];
// PhotoPulse & NaturePulse fall through to the default bundle for now.

const TABS = [
  { id: "google", label: "Google",     Icon: Chrome    },
  { id: "phone",  label: "Phone",      Icon: PhoneIcon },
  { id: "email",  label: "Email link", Icon: Mail      },
];

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 100 }, (_, i) => CURRENT_YEAR - 13 - i); // 13 → 112 yrs old

export default function QuickAuthModal({ onClose, defaultTab = "google", mode: initialMode = "signin" }) {
  const { sendOtp, verifyOtp, sendEmailLink, signInWithGoogleProvider } = useAuth();
  const { geo } = useGeo();
  // Mode is stateful so the "New here? Register" / "Have an account? Sign in"
  // toggle atop the modal can flip between the two flows without unmounting.
  const [mode, setMode] = useState(initialMode);
  const isRegister = mode === "register";

  const [tab, setTab] = useState(defaultTab);
  const [busy, setBusy] = useState(false);
  const [trust, setTrust] = useState(true);

  // Individual vs Business — controls whether the extra "Business
  // Name / Address / Contact person / contact email / phone" fields
  // appear below the person's own info. Same login/email is fine
  // (locked with the user); business is a SEPARATE record created
  // via POST /api/businesses after the personal profile PATCH.
  const [accountType, setAccountType] = useState("individual");
  const isBusiness = isRegister && accountType === "business";
  const [biz, setBiz] = useState({
    name: "",
    contact_person: "",
    contact_email: "",
    contact_phone: "",
    address: "",
    // Universal business type — helps the directory categorise listings.
    // Rendered on ALL sites; the option list is site-aware below.
    business_type: "",
    // Indian tax ID (15-char GSTIN) — only shown when the visitor is on
    // an India-facing site or has picked India in "Confirm your location".
    // Optional at signup; can be added later from the business profile.
    gstin: "",
  });

  // Universal business-type options — trimmed per site so FoodPulse users
  // don't see irrelevant categories like "Photography Studio" and IFT
  // users don't see "Restaurant". The full list still lives in the
  // Business profile page for edit-later.
  const _BUSINESS_TYPES_FOODPULSE = [
    { value: "restaurant",    label: "Restaurant / Cafe" },
    { value: "grocery",       label: "Grocery / Supermarket" },
    { value: "farm",          label: "Farm / Producer" },
    { value: "bakery",        label: "Bakery / Sweets" },
    { value: "catering",      label: "Catering / Tiffin service" },
    { value: "food_truck",    label: "Food truck / Cloud kitchen" },
    { value: "wholesaler",    label: "Wholesaler / Distributor" },
    { value: "other_food",    label: "Other (food-related)" },
  ];
  const _BUSINESS_TYPES_IFT = [
    { value: "temple",        label: "Temple / Religious institution" },
    { value: "matrimony",     label: "Matrimony service" },
    { value: "genealogist",   label: "Genealogist / Family historian" },
    { value: "photographer",  label: "Photographer / Videographer" },
    { value: "printer",       label: "Printer / Memorial cards" },
    { value: "funeral",       label: "Funeral / Cremation service" },
    { value: "translator",    label: "Translator / Scribe" },
    { value: "other_heritage",label: "Other (heritage-related)" },
  ];
  const _BUSINESS_TYPES_GENERIC = [
    { value: "retail",        label: "Retail store" },
    { value: "service",       label: "Service provider" },
    { value: "hospitality",   label: "Hospitality / Hotel" },
    { value: "healthcare",    label: "Healthcare / Clinic" },
    { value: "education",     label: "Education / Tutor" },
    { value: "creative",      label: "Creative / Media" },
    { value: "nonprofit",     label: "Non-profit / NGO" },
    { value: "other",         label: "Other" },
  ];

  // Registration profile fields (only used when mode === "register").
  const [profile, setProfile] = useState({
    first_name: "",
    last_name: "",
    gender: "",
    year_of_birth: "",
    // location — pre-filled from geo detection, editable
    country: "",
    state: "",
    city: "",
    // status flag — flips to true once user hits "Set" on the location panel
    location_confirmed: false,
  });

  // Site-aware policy checklist. IFT visitors must tick every DPDP-aligned
  // policy; Social Pulse visitors tick the canonical bundle. The final map
  // (`{[slug]: bool}`) is POSTed to `/me/policy-acceptance` after OAuth so
  // acceptance is logged on the user document for audit.
  const siteSlug = useMemo(() => {
    try {
      const q = typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("site")
        : null;
      return (resolveSiteSlug({ queryParam: q || "" }) || "").toLowerCase();
    } catch { return ""; }
  }, []);
  const isIft = siteSlug.includes("indianfamilytree");
  const isMftCare = siteSlug.includes("myfamilytree");
  const isFoodPulse = siteSlug.includes("foodpulse");
  // Which option list to show in the Business Type dropdown.
  const businessTypeOptions = isFoodPulse
    ? _BUSINESS_TYPES_FOODPULSE
    : isIft
    ? _BUSINESS_TYPES_IFT
    : _BUSINESS_TYPES_GENERIC;
  // GSTIN field visibility — either on an Indian site OR the visitor
  // has picked India in "Confirm your location".
  const showGstin = isIft || (profile.country || "").toLowerCase().startsWith("india") || (profile.country || "").toLowerCase() === "in";
  // Site-aware brand label used in modal copy so visitors to IFT /
  // MyFamilyTree.care don't see "Social Pulse" branding on the auth
  // modal — kept in sync with SITE_BRAND in Header.jsx.
  const brandLabel = isIft
    ? "IndianFamilyTree.com"
    : isMftCare
      ? "MyFamilyTree.care"
      : "Social Pulse";
  const requiredPolicies = useMemo(
    () => (
      isIft ? REQUIRED_POLICIES_IFT
      : isFoodPulse ? REQUIRED_POLICIES_FOODPULSE
      : REQUIRED_POLICIES_DEFAULT
    ),
    [isIft],
  );
  const [policyAccepts, setPolicyAccepts] = useState({});
  useEffect(() => {
    // Reset acceptance state whenever the required list changes so the
    // user can't carry stale ticks between the two flows.
    setPolicyAccepts({});
  }, [requiredPolicies]);
  const allPoliciesAccepted = requiredPolicies.every((p) => policyAccepts[p.slug]);

  // Pull initial location from geo detector once available.
  useEffect(() => {
    if (!isRegister || !geo) return;
    setProfile((p) => ({
      ...p,
      country: p.country || geo.country_name || "",
      state:   p.state   || geo.region || geo.state || "",
      city:    p.city    || geo.city   || "",
    }));
  }, [isRegister, geo]);

  // Phone
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  const [customCountryCode, setCustomCountryCode] = useState("");
  const [nationalDigits, setNationalDigits] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  // Email
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const dialogRef = useRef(null);

  // ESC to close + click backdrop to close.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const profilePatch = useMemo(() => {
    if (!isRegister) return null;
    const p = {};
    if (profile.first_name.trim()) p.first_name = profile.first_name.trim();
    if (profile.last_name.trim())  p.last_name  = profile.last_name.trim();
    if (profile.gender)            p.gender     = profile.gender;
    if (profile.year_of_birth)     p.year_of_birth = Number(profile.year_of_birth);
    if (profile.location_confirmed) {
      if (profile.country) p.country = profile.country;
      if (profile.state)   p.state   = profile.state;
      if (profile.city)    p.city    = profile.city;
    }
    return Object.keys(p).length ? p : null;
  }, [isRegister, profile]);

  const applyProfilePatch = async (isNewUser) => {
    // Only apply the collected Register fields for FRESH accounts —
    // returning users already have their profile and would be surprised
    // by silent overwrites. Auto-detects via the ``is_new_user`` flag
    // returned from ``/api/auth/firebase/session``.
    if (!profilePatch || !isNewUser) return;
    try {
      await http.patch("/me/profile", profilePatch);
    } catch (e) {
      // Non-fatal — the user is still signed in; they can edit later in profile.
      console.warn("Register: profile PATCH failed", e?.response?.data || e?.message);
    }
  };

  /**
   * Record policy acceptance in the audit trail after a fresh signup.
   * The backend endpoint (POST /api/me/policy-acceptance) appends every
   * acceptance event with IP + user-agent so the platform has evidence
   * of consent per DPDP compliance guidance.
   */
  const recordPolicyAcceptance = async (isNewUser) => {
    if (!isRegister || !isNewUser) return;
    const accepted = requiredPolicies.map((p) => p.slug).filter((s) => policyAccepts[s]);
    if (!accepted.length) return;
    try {
      await http.post("/me/policy-acceptance", {
        policies: accepted,
        site: isIft ? "indianfamilytree" : "socialpulse",
        context: "registration",
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      });
    } catch (e) {
      // Non-fatal — user is signed in; log is best-effort for audit.
      console.warn("Register: policy-acceptance failed", e?.response?.data || e?.message);
    }
  };

  /**
   * If the user picked "Business" during registration, spin up a
   * business_accounts record linked to them. Runs AFTER
   * applyProfilePatch so contact defaults (city/country) already
   * exist on the user doc for the backend to inherit.
   *
   * Idempotent by (owner_id, name) on the backend — a duplicate call
   * (e.g. accidental refresh) returns the existing business rather
   * than creating a second one.
   */
  const createBusinessIfNeeded = async (isNewUser) => {
    if (!isBusiness) return;
    if (!biz.name.trim()) return;   // silent guard, validation already ran
    const payload = {
      name: biz.name.trim(),
      contact_person: biz.contact_person.trim() || undefined,
      contact_email:  biz.contact_email.trim().toLowerCase() || undefined,
      contact_phone:  biz.contact_phone.trim() || undefined,
      address:        biz.address.trim() || undefined,
      business_type:  biz.business_type.trim() || undefined,
      gstin:          biz.gstin.trim().toUpperCase() || undefined,
    };
    try {
      const { data } = await http.post("/businesses", payload);
      if (data?.business?.name) {
        toast.success(`Business created — ${data.business.name}`);
      }
    } catch (e) {
      // Business creation failure is non-fatal — the user is still
      // signed in as an individual and can create/edit the business
      // later from their profile.
      const msg = e?.response?.data?.detail || e?.message || "Business creation failed";
      console.warn("Business creation failed", msg);
      toast.error(`${msg} — you can create the business later from your profile.`);
    }
  };

  /**
   * Register-mode fields are required at the point where the user
   * commits to an OAuth handshake. Returns null when valid, an error
   * string otherwise (surfaced via toast). No-op in signin mode.
   */
  const validateRegisterFields = () => {
    if (!isRegister) return null;
    if (!profile.first_name.trim())      return "Please enter your first name.";
    if (!profile.last_name.trim())       return "Please enter your last name.";
    if (!profile.gender)                 return "Please select your gender.";
    if (!profile.year_of_birth)          return "Please select your year of birth.";
    if (!profile.location_confirmed)     return "Please confirm your location — tap the Set button.";
    if (isBusiness && !biz.name.trim()) return "Please enter the business name.";
    if (!allPoliciesAccepted)            return "Please read and accept every policy to continue.";
    return null;
  };

  // India DPDP auto-stamp: when an IFT user has accepted the full IFT
  // policy bundle (which includes the "Consent & Authorization" and
  // "Deletion & Legacy" policies that legally embody the DPDP 2023
  // consent), we pass `dpdp_consent: true` + `declared_country: "IN"`
  // through to the backend so the user record is stamped correctly
  // without an extra tickbox. Same effect as the explicit checkbox on
  // /login, without the UX friction of a second consent.
  const authExtras = () => ({
    declared_country: isIft
      ? "IN"
      : (profile.country || "").toLowerCase() === "india" ? "IN" : undefined,
    dpdp_consent: isIft && allPoliciesAccepted,
  });

  const doGoogle = async () => {
    const err = validateRegisterFields();
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      const { is_new_user } = await signInWithGoogleProvider(trust, authExtras());
      await applyProfilePatch(is_new_user);
      await recordPolicyAcceptance(is_new_user);
      await createBusinessIfNeeded(is_new_user);
      toast.success(is_new_user ? "Welcome to Social Pulse!" : "Signed in.");
      onClose?.();
    } catch (e) {
      toast.error(e?.message || "Google sign-in failed. Please try again.");
    } finally { setBusy(false); }
  };

  const doPhoneSend = async () => {
    const cc = countryCode === "OTHER"
      ? (customCountryCode.startsWith("+") ? customCountryCode : ("+" + customCountryCode.replace(/\D/g, "")))
      : countryCode;
    const clean = sanitizeToE164(cc, nationalDigits);
    const totalDigits = clean.replace(/\D/g, "");
    if (!cc.match(/^\+\d{1,4}$/)) return toast.error("Pick a country or enter a valid country code like +1, +91, +44.");
    if (totalDigits.length < 8 || totalDigits.length > 15) return toast.error("That number looks incomplete — enter your full local number.");
    const err = validateRegisterFields();
    if (err) { toast.error(err); return; }
    setPhone(clean);
    setBusy(true);
    try {
      await sendOtp(clean);
      setOtpSent(true);
      toast.success("Verification code sent.");
    } catch (e) {
      toast.error(e?.message || "Could not send the code.");
    } finally { setBusy(false); }
  };

  const doPhoneVerify = async () => {
    if (!/^\d{4,8}$/.test(otp.trim())) return toast.error("Enter the numeric code from the SMS.");
    setBusy(true);
    try {
      const { is_new_user } = await verifyOtp(otp.trim(), trust, authExtras());
      await applyProfilePatch(is_new_user);
      await recordPolicyAcceptance(is_new_user);
      await createBusinessIfNeeded(is_new_user);
      toast.success(is_new_user ? "Welcome to Social Pulse!" : "Signed in.");
      onClose?.();
    } catch (e) {
      toast.error(e?.message || "That code was not accepted.");
    } finally { setBusy(false); }
  };

  const doEmailSend = async () => {
    const clean = (email || "").trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(clean)) return toast.error("Please enter a valid email address.");
    const err = validateRegisterFields();
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      // Stash the pending profile + business intent so the magic-link
      // landing page can apply it once the sign-in completes.
      if (isRegister && profilePatch) {
        try { localStorage.setItem("sp:pending-profile", JSON.stringify(profilePatch)); } catch { /* noop */ }
      }
      if (isRegister) {
        // Persist the ticked policy list so the magic-link landing page
        // can POST /me/policy-acceptance once the session cookie exists.
        try {
          const accepted = requiredPolicies.map((p) => p.slug).filter((s) => policyAccepts[s]);
          if (accepted.length) {
            localStorage.setItem("sp:pending-policy-acceptance", JSON.stringify({
              policies: accepted,
              site: isIft ? "indianfamilytree" : (isFoodPulse ? "foodpulse" : "socialpulse"),
              context: "registration",
              // Auto-DPDP consent: the IFT policy bundle includes the
              // explicit "Consent & Authorization" policy, so ticking
              // all boxes is legally equivalent to the DPDP consent
              // checkbox on /login. Landing side stamps `dpdp_consent`.
              dpdp_consent: isIft && allPoliciesAccepted,
              declared_country: isIft ? "IN" : ((profile.country || "").toLowerCase() === "india" ? "IN" : undefined),
            }));
          }
        } catch { /* noop */ }
      }
      if (isBusiness && biz.name.trim()) {
        try {
          localStorage.setItem("sp:pending-business", JSON.stringify({
            name: biz.name.trim(),
            contact_person: biz.contact_person.trim() || undefined,
            contact_email:  biz.contact_email.trim().toLowerCase() || undefined,
            contact_phone:  biz.contact_phone.trim() || undefined,
            address:        biz.address.trim() || undefined,
            business_type:  biz.business_type.trim() || undefined,
            gstin:          biz.gstin.trim().toUpperCase() || undefined,
          }));
        } catch { /* noop */ }
      }
      await sendEmailLink(clean);
      setEmailSent(true);
      toast.success("Magic link sent — open it on this device to finish signing in.");
    } catch (e) {
      toast.error(e?.message || "Could not send the link.");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-start sm:items-center justify-center bg-black/40 px-4 pt-4 pb-4 sm:py-6 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="quick-auth-title"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="quick-auth-modal"
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl border p-5 max-h-[calc(100vh-2rem)] overflow-y-auto my-auto"
        style={{ borderColor: "var(--wofy-border)" }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 opacity-60 hover:opacity-100"
          aria-label="Close"
          data-testid="quick-auth-close"
        >
          <X className="w-5 h-5" />
        </button>
        <h2 id="quick-auth-title" className="font-display text-xl" data-testid="quick-auth-title">
          {isRegister ? `Register & Join ${brandLabel}` : `Sign in to ${brandLabel}`}
        </h2>
        <p className="text-xs opacity-70 mt-1">
          {isRegister
            ? "Tell us who you are, then pick how you want to sign in."
            : "Pick a method — you can add more in your profile later."}
        </p>

        {/* Mode-switch — mirrors what the user drew: "New here? Register" /
            "Already have an account? Sign in". A single click flips the
            modal without unmounting so any typed fields survive. */}
        <button
          type="button"
          onClick={() => setMode(isRegister ? "signin" : "register")}
          className="mt-3 w-full text-xs rounded-lg border px-3 py-2 hover:bg-black/5 transition-colors flex items-center justify-between"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid="quick-auth-mode-toggle"
        >
          <span className="opacity-70">
            {isRegister ? `Already have an account with ${brandLabel}?` : `New to ${brandLabel}?`}
          </span>
          <span className="font-mono uppercase tracking-wider text-[11px]" style={{ color: "var(--wofy-primary)" }}>
            {isRegister ? "Sign / Log in →" : "Register →"}
          </span>
        </button>

        {/* --- Registration profile fields --- */}
        {isRegister && (
          <div className="mt-4 space-y-3" data-testid="quick-auth-register-fields">
            {/* Account-type toggle — matches user's mockup annotation:
                "Register As Individual - Business". */}
            <div data-testid="quick-auth-account-type">
              <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1.5">
                Register as
                <span className="normal-case ml-1.5 opacity-60">— you can register as both, start with either</span>
              </div>
              <div
                className="grid grid-cols-2 gap-1 rounded-lg border p-1"
                style={{ borderColor: "var(--wofy-border)" }}
              >
                {[
                  { value: "individual", label: "Individual", Icon: User },
                  { value: "business",   label: "Business",   Icon: Building2 },
                ].map(({ value, label, Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setAccountType(value)}
                    className="flex items-center justify-center gap-1.5 py-1.5 rounded-md text-sm font-medium transition-colors"
                    style={{
                      background: accountType === value ? "var(--wofy-primary)" : "transparent",
                      color: accountType === value ? "white" : "var(--wofy-text)",
                    }}
                    data-testid={`quick-auth-type-${value}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <label className="block text-xs">
                <span className="opacity-70">First name</span>
                <input
                  type="text"
                  value={profile.first_name}
                  onChange={(e) => setProfile((p) => ({ ...p, first_name: e.target.value }))}
                  placeholder="First name"
                  className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm bg-transparent"
                  style={{ borderColor: "var(--wofy-border)" }}
                  data-testid="quick-auth-first-name"
                  maxLength={80}
                />
              </label>
              <label className="block text-xs">
                <span className="opacity-70">Last name</span>
                <input
                  type="text"
                  value={profile.last_name}
                  onChange={(e) => setProfile((p) => ({ ...p, last_name: e.target.value }))}
                  placeholder="Last name"
                  className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm bg-transparent"
                  style={{ borderColor: "var(--wofy-border)" }}
                  data-testid="quick-auth-last-name"
                  maxLength={80}
                />
              </label>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-xs">
                <span className="opacity-70">Gender</span>
                <select
                  value={profile.gender}
                  onChange={(e) => setProfile((p) => ({ ...p, gender: e.target.value }))}
                  className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm bg-transparent"
                  style={{ borderColor: "var(--wofy-border)" }}
                  data-testid="quick-auth-gender"
                >
                  <option value="">Select…</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </label>
              <label className="block text-xs">
                <span className="opacity-70">Year of birth</span>
                <select
                  value={profile.year_of_birth}
                  onChange={(e) => setProfile((p) => ({ ...p, year_of_birth: e.target.value }))}
                  className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm bg-transparent"
                  style={{ borderColor: "var(--wofy-border)" }}
                  data-testid="quick-auth-year-of-birth"
                >
                  <option value="">Year</option>
                  {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </label>
            </div>

            {/* Location confirm panel */}
            <div
              className="rounded-lg border p-2.5 text-xs"
              style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
              data-testid="quick-auth-location-panel"
            >
              {profile.location_confirmed ? (
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5" style={{ color: "var(--wofy-primary)" }} />
                    <b>{[profile.city, profile.state, profile.country].filter(Boolean).join(", ") || "Location set"}</b>
                  </span>
                  <button
                    type="button"
                    onClick={() => setProfile((p) => ({ ...p, location_confirmed: false }))}
                    className="text-[10px] font-mono uppercase tracking-wider opacity-70 hover:opacity-100"
                    data-testid="quick-auth-location-edit"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 opacity-70">
                    <MapPin className="w-3.5 h-3.5" />
                    <span>Confirm your location</span>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5">
                    <input
                      value={profile.country}
                      onChange={(e) => setProfile((p) => ({ ...p, country: e.target.value }))}
                      placeholder="Country"
                      className="px-2 py-1 rounded border text-xs bg-white"
                      style={{ borderColor: "var(--wofy-border)" }}
                      data-testid="quick-auth-country"
                    />
                    <input
                      value={profile.state}
                      onChange={(e) => setProfile((p) => ({ ...p, state: e.target.value }))}
                      placeholder="State"
                      className="px-2 py-1 rounded border text-xs bg-white"
                      style={{ borderColor: "var(--wofy-border)" }}
                      data-testid="quick-auth-state"
                    />
                    <input
                      value={profile.city}
                      onChange={(e) => setProfile((p) => ({ ...p, city: e.target.value }))}
                      placeholder="City"
                      className="px-2 py-1 rounded border text-xs bg-white"
                      style={{ borderColor: "var(--wofy-border)" }}
                      data-testid="quick-auth-city"
                    />
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setProfile((p) => ({ ...p, location_confirmed: true }))}
                      disabled={!profile.country && !profile.state && !profile.city}
                      className="text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full disabled:opacity-40"
                      style={{ background: "var(--wofy-primary)", color: "white" }}
                      data-testid="quick-auth-location-set"
                    >
                      Set
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* --- Business-only fields --- */}
            {isBusiness && (
              <div
                className="rounded-lg border p-3 space-y-2"
                style={{ borderColor: "var(--wofy-primary)", background: "rgba(178,93,56,0.04)" }}
                data-testid="quick-auth-business-fields"
              >
                <div className="text-[11px] font-mono uppercase tracking-wider" style={{ color: "var(--wofy-primary)" }}>
                  <Building2 className="w-3 h-3 inline-block mr-1" />
                  Business details
                  <span className="normal-case ml-1 opacity-60">— you can add more from your business profile later</span>
                </div>
                <label className="block text-xs">
                  <span className="opacity-70">Business name <span className="text-red-500">*</span></span>
                  <input
                    type="text"
                    value={biz.name}
                    onChange={(e) => setBiz((b) => ({ ...b, name: e.target.value }))}
                    placeholder="e.g. Acme Bakery"
                    className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm bg-white"
                    style={{ borderColor: "var(--wofy-border)" }}
                    data-testid="quick-auth-biz-name"
                    maxLength={200}
                  />
                </label>
                <label className="block text-xs">
                  <span className="opacity-70">Business address</span>
                  <input
                    type="text"
                    value={biz.address}
                    onChange={(e) => setBiz((b) => ({ ...b, address: e.target.value }))}
                    placeholder="Street, city — enough for customers to find you"
                    className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm bg-white"
                    style={{ borderColor: "var(--wofy-border)" }}
                    data-testid="quick-auth-biz-address"
                    maxLength={500}
                  />
                </label>
                <label className="block text-xs">
                  <span className="opacity-70">Contact person</span>
                  <input
                    type="text"
                    value={biz.contact_person}
                    onChange={(e) => setBiz((b) => ({ ...b, contact_person: e.target.value }))}
                    placeholder="Auto-filled from your name — edit if different"
                    className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm bg-white"
                    style={{ borderColor: "var(--wofy-border)" }}
                    data-testid="quick-auth-biz-contact-person"
                    maxLength={120}
                  />
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block text-xs">
                    <span className="opacity-70">Business email</span>
                    <input
                      type="email"
                      value={biz.contact_email}
                      onChange={(e) => setBiz((b) => ({ ...b, contact_email: e.target.value }))}
                      placeholder="hello@acmebakery.com"
                      className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm bg-white"
                      style={{ borderColor: "var(--wofy-border)" }}
                      data-testid="quick-auth-biz-email"
                      maxLength={200}
                    />
                  </label>
                  <label className="block text-xs">
                    <span className="opacity-70">Business phone</span>
                    <input
                      type="tel"
                      value={biz.contact_phone}
                      onChange={(e) => setBiz((b) => ({ ...b, contact_phone: e.target.value }))}
                      placeholder={isIft ? "+91 98765 43210" : "+1 555 123 4567"}
                      className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm bg-white"
                      style={{ borderColor: "var(--wofy-border)" }}
                      data-testid="quick-auth-biz-phone"
                      maxLength={40}
                    />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block text-xs">
                    <span className="opacity-70">Business type</span>
                    <select
                      value={biz.business_type}
                      onChange={(e) => setBiz((b) => ({ ...b, business_type: e.target.value }))}
                      className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm bg-white"
                      style={{ borderColor: "var(--wofy-border)" }}
                      data-testid="quick-auth-biz-type"
                    >
                      <option value="">Select…</option>
                      {businessTypeOptions.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </label>
                  {showGstin && (
                    <label className="block text-xs">
                      <span className="opacity-70">GSTIN <span className="opacity-50 normal-case">(optional)</span></span>
                      <input
                        type="text"
                        value={biz.gstin}
                        onChange={(e) => setBiz((b) => ({ ...b, gstin: e.target.value.toUpperCase() }))}
                        placeholder="15-char GST number"
                        className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm bg-white font-mono uppercase tracking-wider"
                        style={{ borderColor: "var(--wofy-border)" }}
                        data-testid="quick-auth-biz-gstin"
                        maxLength={15}
                        pattern="[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]"
                        title="15-char GSTIN, e.g. 27ABCDE1234F1Z5"
                      />
                    </label>
                  )}
                </div>
                <p className="text-[10px] opacity-70 italic">
                  You'll sign in with your own email/phone — the business gets its own profile page
                  where you can add team members with Manager or Editor access.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Firebase reCAPTCHA anchor for phone OTP */}
        <div id="firebase-recaptcha" />

        {/* Tabs */}
        <div
          className="mt-4 grid grid-cols-3 gap-1 p-1 rounded-full border"
          style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
          role="tablist"
        >
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className="inline-flex items-center justify-center gap-1.5 py-1.5 rounded-full text-xs font-medium transition-colors"
              style={{
                background: tab === t.id ? "var(--wofy-primary)" : "transparent",
                color: tab === t.id ? "white" : "var(--wofy-text)",
              }}
              data-testid={`quick-auth-tab-${t.id}`}
            >
              <t.Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          ))}
        </div>

        {/* Panel */}
        <div className="mt-4 min-h-[130px]">
          {tab === "google" && (
            <div className="space-y-3">
              <button
                type="button"
                onClick={doGoogle}
                disabled={busy || (isRegister && !allPoliciesAccepted)}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full border py-2.5 text-sm font-medium hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid="quick-auth-google"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Chrome className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />}
                {busy ? "Signing in…" : "Continue with Google"}
              </button>
              <p className="text-[11px] opacity-60 text-center">A Google popup will open — grant access to sign in instantly.</p>
            </div>
          )}

          {tab === "phone" && (
            <div className="space-y-3">
              {!otpSent ? (
                <>
                  <label className="block text-xs opacity-70">Phone number</label>
                  <div className="flex items-stretch gap-2">
                    {/* Country-code picker */}
                    <div className="flex items-center rounded-lg border px-2" style={{ borderColor: "var(--wofy-border)" }}>
                      <select
                        data-testid="quick-auth-phone-country"
                        value={countryCode === "OTHER" ? "OTHER" : `${countryCode}__${COUNTRY_CODES.find((c) => c.code === countryCode && (c.key || "") === "")?.label || countryCode}`}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === "OTHER") { setCountryCode("OTHER"); return; }
                          const [cc] = v.split("__");
                          setCountryCode(cc);
                        }}
                        className="bg-transparent outline-none text-sm font-mono py-2 pr-1"
                        aria-label="Country dial code"
                      >
                        {COUNTRY_CODES.map((c) => (
                          <option key={`${c.code}-${c.key || c.label}`} value={`${c.code}__${c.label}`}>
                            {c.flag} {c.label} {c.code}
                          </option>
                        ))}
                        <option value="OTHER">🌐 Other…</option>
                      </select>
                    </div>

                    {countryCode === "OTHER" && (
                      <input
                        data-testid="quick-auth-phone-custom-cc"
                        value={customCountryCode}
                        onChange={(e) => setCustomCountryCode(e.target.value.replace(/[^\d+]/g, "").slice(0, 5))}
                        placeholder="+CC"
                        className="w-20 rounded-lg border px-2 outline-none bg-transparent font-mono text-sm"
                        style={{ borderColor: "var(--wofy-border)" }}
                        inputMode="tel"
                        aria-label="Custom country code"
                      />
                    )}

                    <input
                      type="tel"
                      value={formatNational(nationalDigits, countryCode === "OTHER" ? "" : countryCode)}
                      onChange={(e) => setNationalDigits(e.target.value.replace(/\D/g, ""))}
                      placeholder={(NATIONAL_FORMATS[countryCode] || {}).hint || "phone number"}
                      className="flex-1 px-3 py-2 rounded-lg border text-sm bg-transparent font-mono"
                      style={{ borderColor: "var(--wofy-border)" }}
                      data-testid="quick-auth-phone-input"
                      autoComplete="tel-national"
                      inputMode="tel"
                    />
                  </div>
                  <p className="text-[11px] opacity-60">
                    {countryCode === "OTHER"
                      ? "Enter your country code (e.g. +81) and full phone number."
                      : `Format: ${(NATIONAL_FORMATS[countryCode] || {}).hint || "your local number"}`}
                  </p>
                  <button
                    type="button"
                    onClick={doPhoneSend}
                    disabled={busy || !nationalDigits || (isRegister && !allPoliciesAccepted)}
                    className="w-full wofy-btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="quick-auth-phone-send"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                    Send verification code
                  </button>
                </>
              ) : (
                <>
                  <p className="text-xs opacity-70">Code sent to <b>{phone}</b>.</p>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    placeholder="6-digit code"
                    className="w-full px-3 py-2 rounded-lg border text-sm bg-transparent"
                    style={{ borderColor: "var(--wofy-border)" }}
                    data-testid="quick-auth-phone-otp"
                  />
                  <div className="flex gap-2">
                    <button type="button" onClick={() => { setOtpSent(false); setOtp(""); }} className="wofy-btn-ghost text-xs px-2">Change number</button>
                    <button
                      type="button"
                      onClick={doPhoneVerify}
                      disabled={busy || !otp}
                      className="flex-1 wofy-btn-primary text-sm"
                      data-testid="quick-auth-phone-verify"
                    >
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      Verify &amp; sign in
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {tab === "email" && (
            <div className="space-y-3">
              {!emailSent ? (
                <>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full px-3 py-2 rounded-lg border text-sm bg-transparent"
                    style={{ borderColor: "var(--wofy-border)" }}
                    data-testid="quick-auth-email-input"
                  />
                  <button
                    type="button"
                    onClick={doEmailSend}
                    disabled={busy || !email}
                    className="w-full wofy-btn-primary text-sm"
                    data-testid="quick-auth-email-send"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                    Send magic link
                  </button>
                </>
              ) : (
                <div className="text-sm">
                  <p><b>Check your inbox.</b></p>
                  <p className="opacity-70 mt-1">Open the link on this device to finish signing in. You can close this dialog.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Policy acceptance checklist — required for fresh registrations.
            Site-aware: IFT surfaces the full DPDP bundle; Social Pulse the
            canonical Terms + Privacy + Cookie + Memorial set. Every box
            must be ticked before OAuth can proceed. Records are logged
            to /me/policy-acceptance for compliance evidence. */}
        {isRegister && (
          <div
            className="mt-4 rounded-xl border p-3"
            style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
            data-testid="quick-auth-policy-checklist"
          >
            <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-2">
              Please read &amp; accept
              <span className="normal-case ml-1.5 opacity-60">— every item is required to create your account</span>
            </div>
            <div className="space-y-1.5">
              {requiredPolicies.map((p) => (
                <label
                  key={p.slug}
                  className="flex items-start gap-2 text-xs leading-snug cursor-pointer"
                  data-testid={`quick-auth-policy-${p.slug}`}
                >
                  <input
                    type="checkbox"
                    checked={!!policyAccepts[p.slug]}
                    onChange={(e) => setPolicyAccepts((prev) => ({ ...prev, [p.slug]: e.target.checked }))}
                    className="mt-0.5 h-4 w-4 accent-current shrink-0"
                    data-testid={`quick-auth-policy-cb-${p.slug}`}
                  />
                  <span>
                    I have read and accept the{" "}
                    <Link
                      to={`/policies/${p.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline font-medium"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`quick-auth-policy-link-${p.slug}`}
                    >
                      {p.label}
                    </Link>
                    .
                  </span>
                </label>
              ))}
            </div>
            {!allPoliciesAccepted && (
              <div className="mt-2 text-[10px] opacity-60">
                Tick every box to enable the sign-up buttons.
              </div>
            )}
          </div>
        )}

        {/* Trust device */}
        <label className="flex items-center gap-2 mt-4 text-xs opacity-80">
          <input
            type="checkbox"
            checked={trust}
            onChange={(e) => setTrust(e.target.checked)}
            data-testid="quick-auth-trust"
          />
          Keep me signed in on this device (12h idle limit)
        </label>

        <p className="text-[11px] opacity-60 mt-4 text-center">
          New here? Signing in also creates your account.
        </p>
      </div>
    </div>
  );
}
